import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { generalConfigFromGlobal } from '../../../shared/workspace-element-context';
import { FormViewService } from '../../../admin/components/form-view/form-view.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { EFileService } from '../../efile.service';
import { SubmissionService } from '../../submission-data/submission.service';
import {
  EfileGridCallbackEvent,
  EfileGridConfig,
  EfileGridDirective,
} from '../../shared/efile-grid.directive';

const PRODUCTION_ACCEPTED = 'Production Accepted';

const USER_TYPE_MAP: Record<string, number> = {
  USER: 1,
  EDITOR: 2,
  ADMIN: 3,
  DEVELOPER: 4,
  SUPER_ADMIN: 5,
};

interface PendingConfirm {
  callName: 'generateXML' | 'generateSubmissionXML';
  message: string;
  rowData?: any;
}

/**
 * Angular 12 port of `EfileKeyXmlGenController` (8473 Signature / Key XML Gen).
 *
 * Legacy sources:
 *   - ui/app/components/efile/efile.js L607-L613  (.state 'submissions-dasboard.submission.keyXmlGen')
 *   - ui/app/components/efile/efileElements/key-xml-gen.html  (gizmoGrid container with hidden-span metadata)
 *   - ui/app/components/efile/efileElements/EfileElementsController.js L4818-L5008 (~200 lines)
 *
 * What the legacy controller does:
 *   - ADMIN-only access: calls `EfileFactory.hasAccess('ADMIN')` and redirects to
 *     the dashboard state when the user fails the check (toast nothing).
 *   - Mounts a gizmoGrid against `loadGridJson?grid_id=6127&action_code=ccfa3x`
 *     with `submission_id` as the only filter param (see L4976-L4992).
 *   - Wires three ctrlFn callbacks:
 *       generateXML            → confirm modal → EfileFactory.validate(...)
 *       generateSubmissionXML  → confirm modal → EfileFactory.generateSubmissionXML(...)
 *       downloadXML            → FormViewFactory.downloadXMLorZIP(...) (no confirm)
 *   - Blocks `generateXML` + `generateSubmissionXML` when the submission status
 *     is `Production Accepted` (warning toast, no API call).
 *
 * Migrated wiring:
 *   - `efileGrid` directive owns the gizmoGrid lifecycle. We pass it the
 *     legacy gridId/actionCode and submission_id as `extraParams`, plus a
 *     submission-scoped `cacheKey` so refresh-on-tab-return mirrors the
 *     legacy `workspaceFactory.customDataCache` keyed by `EFILE_SCRUBBING_<id>`.
 *   - Confirmations use the same inline-modal pattern as `EfileSetupComponent`
 *     (no AngularJS modal stack, no MatDialog dependency).
 *   - Generate calls go through `EFileService.validateSubmission` / `.generateSubmissionXML`.
 *   - Download calls go through `FormViewService.downloadXMLorZIP` and trigger
 *     the same `<a download>` flow as the legacy controller.
 */
@Component({
    selector: 'gtw-efile-key-xml-gen',
    templateUrl: './efile-key-xml-gen.component.html',
    styleUrls: ['./efile-key-xml-gen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EfileKeyXmlGenComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  // ---------------------------------------------------------------------------
  // Legacy grid metadata — load vs export use different identifiers:
  //   load   → grid_id=6127, action_code=ccfa3x (EfileKeyXmlGenController#getData)
  //   export → action_id=33070, grid_id=6074, screen_key=11212 (key-xml-gen.html
  //            hidden spans, read by gizmoGrid exportExcel)
  // ---------------------------------------------------------------------------
  private static readonly LEGACY_GRID_ID = '6127';
  private static readonly LEGACY_ACTION_CODE = 'ccfa3x';
  private static readonly LEGACY_EXPORT_ACTION_ID = 33070;
  private static readonly LEGACY_EXPORT_GRID_ID = '6074';
  private static readonly LEGACY_EXPORT_SCREEN_KEY = 11212;
  private static readonly JCD_KEY_FEDERAL = 250;
  private static readonly JCD_KEY_STATE = 253;

  gridConfig: EfileGridConfig | null = null;
  showAccessDenied = false;
  noSubmission = false;

  // Confirm-modal state — direct port of the legacy
  // `app/templates/modals/confirm-action.html` `$uibModal` flow.
  // Now rendered through the shared <gtw-confirmation-modal>.
  showConfirm = false;
  saving = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to proceed?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orangeDark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pending: PendingConfirm | null = null;

  @ViewChild('keyXmlGrid') gridRef?: EfileGridDirective;

  private subs = new Subscription();

  constructor(
    private cdr: ChangeDetectorRef,
    private submissionService: SubmissionService,
    private gtwGlobal: GlobalService,
    private appService: AppService,
    private snackbar: GtwSnackbarService,
    private efileService: EFileService,
    private formViewService: FormViewService,
  ) {}

  ngOnInit(): void {
    this.evaluateAccessAndBuildGrid();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ---------------------------------------------------------------------------
  // Access check + grid bootstrap
  // ---------------------------------------------------------------------------

  /**
   * Mirrors the legacy guard:
   *   if (!EfileFactory.hasAccess('ADMIN')) $state.go('...dashboard', ...); return;
   *
   * `EfileFactory.hasAccess(req) = USER_SETTINGS.user.client.user_type_key >= userMap[req]`
   * (EfileService.js L136). We can't navigate from a leaf component without a
   * router dependency here, so we surface a non-blocking placeholder instead —
   * the sidebar entry is already gated by the same access tier in
   * `submission-detail.component`.
   */
  private hasAdminAccess(): boolean {
    const userType = this.appService?.USER_SETTINGS?.user?.client?.user_type_key;
    return Number(userType) >= USER_TYPE_MAP['ADMIN'];
  }

  private evaluateAccessAndBuildGrid(): void {
    if (!this.hasAdminAccess()) {
      this.showAccessDenied = true;
      this.cdr.markForCheck();
      return;
    }

    const submission = this.submissionService.activeSubmission;
    if (!submission?.SUBMISSION_ID) {
      this.noSubmission = true;
      this.cdr.markForCheck();
      return;
    }

    this.gridConfig = {
      actionCode: EfileKeyXmlGenComponent.LEGACY_ACTION_CODE,
      gridId: EfileKeyXmlGenComponent.LEGACY_GRID_ID,
      exportActionId: EfileKeyXmlGenComponent.LEGACY_EXPORT_ACTION_ID,
      exportGridId: EfileKeyXmlGenComponent.LEGACY_EXPORT_GRID_ID,
      exportScreenKey: EfileKeyXmlGenComponent.LEGACY_EXPORT_SCREEN_KEY,
      autoLoad: true,
      extraParams: { submission_id: submission.SUBMISSION_ID },
      // Legacy cache key was `EFILE_SCRUBBING_<SUBMISSION_ID>`
      // (EfileElementsController.js L4824). Same scoping here.
      cacheKey: `efile/key-xml-gen/${submission.SUBMISSION_ID}`,
    };
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // gizmoGrid callback dispatcher — mirrors EfileKeyXmlGenController.vm.ctrlFn
  // (EfileElementsController.js L4840-L4872).
  // ---------------------------------------------------------------------------

  onGridCallback(event: EfileGridCallbackEvent): void {
    if (!event) return;

    switch (event.callName) {
      case 'generateXML':
        this.handleGenerateXML(event);
        return;
      case 'generateSubmissionXML':
        this.handleGenerateSubmissionXML(event);
        return;
      case 'downloadXML':
        this.handleDownloadXML(event);
        return;
      default:
        // Unknown callbacks — let other handlers (or no-op) deal with it. The
        // legacy controller silently ignores unknown callNames so we do the
        // same here.
        return;
    }
  }

  private handleGenerateXML(event: EfileGridCallbackEvent): void {
    const submission = this.submissionService.activeSubmission;
    if (!submission) return;

    // `Submission` model does not declare `SUBMISSION_STATUS`, but the legacy
    // grid payload always returns it. Cast through `any` to mirror the rest of
    // the migrated efile screens (pdf-upload, return-review, dashboard).
    if ((submission as any).SUBMISSION_STATUS === PRODUCTION_ACCEPTED) {
      this.snackbar.openSnackBar(
        'Cannot generate XML for Production Accepted Submission',
        'gtw-snackbar--yellow',
      );
      return;
    }

    this.pending = {
      callName: 'generateXML',
      message: 'Are you sure you want to generate the XML?',
      rowData: event?.data?.rowData ?? event?.data,
    };
    this.openConfirmModal(this.pending.message);
  }

  private handleGenerateSubmissionXML(event: EfileGridCallbackEvent): void {
    const submission = this.submissionService.activeSubmission;
    if (!submission) return;

    if ((submission as any).SUBMISSION_STATUS === PRODUCTION_ACCEPTED) {
      this.snackbar.openSnackBar(
        'Cannot generate XML for Production Accepted Submission',
        'gtw-snackbar--yellow',
      );
      return;
    }

    this.pending = {
      callName: 'generateSubmissionXML',
      message: 'Are you sure you want to generate the Submission XML?',
      rowData: event?.data?.rowData ?? event?.data,
    };
    this.openConfirmModal(this.pending.message);
  }

  /**
   * Open the shared confirmation modal with the given message. Replaces the
   * old inline overlay (legacy confirm-action.html / $uibModal flow).
   */
  private openConfirmModal(message: string): void {
    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      message,
    };
    this.showConfirm = true;
    this.cdr.markForCheck();
  }

  private handleDownloadXML(event: EfileGridCallbackEvent): void {
    const submission = this.submissionService.activeSubmission;
    if (!submission?.SUBMISSION_ID) return;

    const rowData = event?.data?.rowData ?? event?.data ?? {};
    const keyId = rowData?.KEY_ID;
    const leType = rowData?.LE_TYPE;
    if (!keyId) {
      this.snackbar.openSnackBar(
        'No row selected for download.',
        'gtw-snackbar--yellow',
      );
      return;
    }

    const bulkPdfUrl =
      generalConfigFromGlobal(this.gtwGlobal).bulkPdfEngine ||
      this.efileService.baseURLs.bulkPdfEngine ||
      '';

    this.formViewService
      .downloadXMLorZIP(bulkPdfUrl, submission.SUBMISSION_ID, keyId, leType, 'xml').pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: ArrayBuffer) => {
          if (!data || data.byteLength === 0) {
            this.snackbar.openSnackBar(
              'No file available to download',
              'gtw-snackbar--yellow',
            );
            return;
          }
          const blob = new Blob([data], { type: 'application/xml' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download =
            `${submission.TAX_YEAR}_${submission.RETURN_TYPE}_` +
            `${submission.TAX_PAYER_NAME}(${submission.TAX_PAYER}).xml`;
          link.click();
          link.remove();
        },
        error: (err) => {
          console.error('[KeyXmlGen] Error downloading XML:', err);
          this.snackbar.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Confirm dialog handlers
  // ---------------------------------------------------------------------------

  onCancelConfirm(): void {
    this.showConfirm = false;
    this.pending = null;
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    if (!this.pending || this.saving) return;

    const pending = this.pending;
    this.showConfirm = false;
    this.saving = true;
    this.cdr.markForCheck();

    const submission = this.submissionService.activeSubmission;
    if (!submission?.SUBMISSION_ID) {
      this.saving = false;
      this.pending = null;
      this.cdr.markForCheck();
      return;
    }

    if (pending.callName === 'generateXML') {
      this.runGenerateXML(submission, pending.rowData);
    } else if (pending.callName === 'generateSubmissionXML') {
      this.runGenerateSubmissionXML(submission);
    }
  }

  /** Unwrap validate/generate responses: legacy `$http` used `response.data`; some interceptors keep that shape. */
  private static unwrapEfileApiBody(response: any): any {
    if (response == null) {
      return response;
    }
    if (response.callSuccess !== undefined || response.errorMessage !== undefined) {
      return response;
    }
    if (response.data != null) {
      return response.data;
    }
    return response;
  }

  /**
   * Direct port of `EfileKeyXmlGenController#generateXML`
   * (EfileElementsController.js L4498-L4517). Builds the `taxEntity` payload
   * and POSTs it to `/validate?submissionId=…&keyId=…`.
   *
   * Keep field types/shape aligned with AngularJS: raw `submission_id`, `key_id`,
   * `tax_year`, `scenario` (from `SCENARIO_ID`), `jcd_key`, and `jobsList` with
   * `job_id` — do not coerce missing values to `0` (that breaks the API).
   */
  private runGenerateXML(submission: any, rowData: any): void {
    const row = rowData?.rowData ?? rowData ?? {};
    const subType = String(
      submission?.submissionType ?? submission?.SUBMISSION_TYPE ?? '',
    ).toLowerCase();
    const jcdKey =
      subType === 'federal'
        ? EfileKeyXmlGenComponent.JCD_KEY_FEDERAL
        : EfileKeyXmlGenComponent.JCD_KEY_STATE;

    const taxEntity = {
      submission_id: submission.SUBMISSION_ID,
      key_id: row.KEY_ID ?? row.key_id,
      tax_year: submission.TAX_YEAR ?? submission.tax_year,
      scenario: row.SCENARIO_ID ?? row.SCENARIO ?? row.scenario,
      jcd_key: jcdKey,
      jobsList: [
        {
          job_id: row.JOB_ID ?? row.job_id,
        },
      ],
    };

    const keyIdForQuery =
      row.KEY_ID != null && row.KEY_ID !== '' ? String(row.KEY_ID) : '';

    this.subs.add(
      this.efileService
        .validateSubmission(String(submission.SUBMISSION_ID), keyIdForQuery, taxEntity)
        .subscribe({
          next: (response: any) => {
            this.saving = false;
            this.pending = null;
            const payload = EfileKeyXmlGenComponent.unwrapEfileApiBody(response);
            if (payload && String(payload.callSuccess) === '1') {
              this.snackbar.openSnackBar(
                'XML Generation was Initiated!',
                'gtw-snackbar--green',
              );
              if (this.gridRef) this.gridRef.refresh();
            } else {
              this.snackbar.openSnackBar(
                payload?.errorMessage || 'Failed to initiate XML Generation.',
                'gtw-snackbar--red',
              );
            }
            this.cdr.markForCheck();
          },
          error: (err) => {
            this.saving = false;
            this.pending = null;
            console.error('[KeyXmlGen] generateXML error:', err);
            this.snackbar.openSnackBar(
              'Failed to initiate XML Generation.',
              'gtw-snackbar--red',
            );
            this.cdr.markForCheck();
          },
        }),
    );
  }

  /**
   * Direct port of `EfileKeyXmlGenController#generateSubmissionXML`
   * (EfileElementsController.js L4909-L4917). Calls
   * `EfileFactory.generateSubmissionXML(submissionId)`.
   */
  private runGenerateSubmissionXML(submission: any): void {
    this.subs.add(
      this.efileService.generateSubmissionXML(submission.SUBMISSION_ID).subscribe({
        next: (response: any) => {
          this.saving = false;
          this.pending = null;
          const payload = EfileKeyXmlGenComponent.unwrapEfileApiBody(response);
          if (payload && String(payload.callSuccess) === '1') {
            this.snackbar.openSnackBar(
              'XML Generation was completed!',
              'gtw-snackbar--green',
            );
            if (this.gridRef) this.gridRef.refresh();
          } else {
            this.snackbar.openSnackBar(
              payload?.errorMessage || 'Failed to generate Submission XML.',
              'gtw-snackbar--red',
            );
          }
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.saving = false;
          this.pending = null;
          console.error('[KeyXmlGen] generateSubmissionXML error:', err);
          this.snackbar.openSnackBar(
            'Failed to generate Submission XML.',
            'gtw-snackbar--red',
          );
          this.cdr.markForCheck();
        },
      }),
    );
  }
}
